/* One row of /api/onlineshop/price-change-products (PriceChangeController). */
export interface PriceChangeRow {
  id: number;
  /** The day of the change, d.m.Y. */
  updated: string;
  brand_name: string;
  brand_erp_id: number;
  product_size_erp_id: number;
  product_size_name: string;
  /** The product of the size; groups its sizes (one row per product). */
  product_erp_id?: number | null;
  /** Shop bestseller ranks, lower is better; null without a rank. */
  product_ranking?: number | null;
  ranking?: number | null;
  /** The customer's regular price of the size; null without a customer. */
  price: number | null;
  price_trend: string | null;
  /** Always positive, the direction is price_trend; null while the ERP does
      not send it. */
  price_change_percent: number | null;
}

export type PriceChangeFilter = 'all' | 'stocked' | 'down';

export interface PriceChangeGroup {
  /** As the endpoint sends it, d.m.Y. */
  date: string;
  /** YYYY-MM-DD for <time dateTime>, null when the date has another shape. */
  isoDate: string | null;
  rows: PriceChangeRow[];
}

function matchesFilter(
  row: PriceChangeRow,
  filter: PriceChangeFilter,
  stocked: ReadonlySet<number>
): boolean {
  switch (filter) {
    case 'stocked':
      return stocked.has(row.product_size_erp_id);
    case 'down':
      return row.price_trend === 'down';
    default:
      return true;
  }
}

/* The rows of a filter, grouped by day, one row per product and day.

   The endpoint sends the changes newest first, then by brand and article
   (PriceChangeRepository::findAllSorted), the order of the dashboard's lists
   (Marketing 29.10.2026: brand as the second sort key); consecutive rows of
   one day form a group.

   One row per product, as in the dashboard's "Info overview" (Marketing
   28.10.2026, selectStatusRows): of the sizes of a product changed on the
   same day, the row shows the one the customer stocks, otherwise the shop's
   best seller. The filter applies first, so "Price down" keeps a size whose
   price went down.

   Within a day the customer's own items come first, the rows marked "In your
   range", then the others, each part in the endpoint order (Marketing
   04.11.2026: dealers come to re-price their own items or to find a cheaper
   buy). This runs after the size choice, so a product counts as own when the
   customer stocks any of its changed sizes. In "Your items" every row is
   own and nothing moves. */
export function groupPriceChanges(
  rows: PriceChangeRow[],
  filter: PriceChangeFilter,
  stocked: ReadonlySet<number>
): PriceChangeGroup[] {
  const groups: PriceChangeGroup[] = [];
  let current: PriceChangeRow[] = [];
  const flush = () => {
    if (current.length > 0) {
      groups.push({
        date: current[0].updated,
        isoDate: toIsoDate(current[0].updated),
        rows: stockedFirst(onePerProduct(current, stocked), stocked),
      });
    }
  };
  for (const row of rows) {
    if (!matchesFilter(row, filter, stocked)) {
      continue;
    }
    if (current.length > 0 && current[0].updated !== row.updated) {
      flush();
      current = [];
    }
    current.push(row);
  }
  flush();
  return groups;
}

/* Keeps one size per product at the position of the product's first size;
   rows without a product stand for themselves. */
function onePerProduct(
  rows: PriceChangeRow[],
  stocked: ReadonlySet<number>
): PriceChangeRow[] {
  const sizesOfProduct = new Map<number, PriceChangeRow[]>();
  const order: (PriceChangeRow | number)[] = [];
  for (const row of rows) {
    const product = row.product_erp_id;
    if (product == null) {
      order.push(row);
      continue;
    }
    const sizes = sizesOfProduct.get(product);
    if (sizes) {
      sizes.push(row);
    } else {
      sizesOfProduct.set(product, [row]);
      order.push(product);
    }
  }
  return order.map((entry) =>
    typeof entry === 'number'
      ? pickSize(sizesOfProduct.get(entry)!, stocked)
      : entry
  );
}

function pickSize(
  sizes: PriceChangeRow[],
  stocked: ReadonlySet<number>
): PriceChangeRow {
  const stockedSize = sizes.find((row) => stocked.has(row.product_size_erp_id));
  if (stockedSize) {
    return stockedSize;
  }
  return sizes.reduce((best, row) =>
    compareRank(row.ranking, best.ranking) < 0 ? row : best
  );
}

/* The stocked rows first, then the others; a stable partition, so both parts
   keep their order. */
function stockedFirst(
  rows: PriceChangeRow[],
  stocked: ReadonlySet<number>
): PriceChangeRow[] {
  return [
    ...rows.filter((row) => stocked.has(row.product_size_erp_id)),
    ...rows.filter((row) => !stocked.has(row.product_size_erp_id)),
  ];
}

/* Lower rank first, missing ranks last; equal ranks keep the earlier row. */
function compareRank(
  a: number | null | undefined,
  b: number | null | undefined
): number {
  if (a == null) {
    return b == null ? 0 : 1;
  }
  if (b == null) {
    return -1;
  }
  return a - b;
}

function toIsoDate(date: string): string | null {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(date);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
}
